import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Target, Eye, Heart, Users } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export const About = () => {
  const { t } = useTranslation();

  const values = [
    {
      icon: Target,
      title: 'Mission',
      description: t('about.mission'),
    },
    {
      icon: Eye,
      title: 'Vision',
      description: 'Essere il partner di riferimento nel settore medicale, riconosciuti per innovazione e qualità.',
    },
    {
      icon: Heart,
      title: 'Values',
      description: 'Integrità, eccellenza, innovazione e dedizione alla salute dei pazienti guidano ogni nostra azione.',
    },
    {
      icon: Users,
      title: 'Team',
      description: 'Un team di professionisti esperti e appassionati, dedicati al successo dei nostri clienti.',
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-dark overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary rounded-full blur-3xl animate-float"></div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="font-display font-bold text-5xl lg:text-6xl mb-6 text-white">
              {t('about.title')}
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              {t('about.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-display font-bold text-4xl mb-6 text-foreground">
                La Nostra Storia
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  {t('about.description')}
                </p>
                <p>
                  Dal 1995, Gmed Srl si è affermata come leader nel mercato italiano ed europeo, 
                  fornendo soluzioni medicali innovative a ospedali, cliniche e professionisti sanitari.
                </p>
                <p>
                  Il nostro impegno costante nell'innovazione tecnologica e nella qualità del servizio 
                  ci ha permesso di costruire relazioni durature con i nostri clienti, basate sulla fiducia 
                  e sull'eccellenza.
                </p>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden glass-card">
                <img
                  src="https://images.unsplash.com/photo-1758691462848-ba1e929da259?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwzfHxoZWFsdGhjYXJlJTIwaW5ub3ZhdGlvbnxlbnwwfHx8fDE3Njk2ODEwMDV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Il team Gmed al lavoro su soluzioni IT per la sanità"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>

          {/* Values Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="glass-card h-full">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <value.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="font-display font-semibold text-xl mb-2 text-foreground">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;